import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { Loader2, Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAuthStore } from '@/store/authStore'
import { useLanguage } from '@/store/language-store'
import { useTheme } from '@/components/ThemeProvider'
import { cn } from '@/lib/utils'
import LogoDark from '../assets/logo-dark.svg?url'
import LogoLight from '../assets/logo-light.svg?url'

export default function ForgotPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { language } = useLanguage()
  const { theme } = useTheme()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { resetPassword } = useAuthStore()

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      toast.error(t('auth.invalidEmail'))
      return
    }

    setIsLoading(true)
    
    try {
      const { success, error } = await resetPassword(email)
      
      if (success) {
        toast.success(t('auth.resetPasswordSuccess'), {
          description: t('auth.resetPasswordNote')
        })
        
        // Navigate back to login after 2 seconds
        setTimeout(() => {
          navigate('/auth/login')
        }, 2000)
      } else {
        if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
          if (error.message.includes('too many requests')) {
            toast.error(t('auth.tooManyRequests'))
          } else if (error.message.includes('network')) {
            toast.error(t('auth.networkError'))
          } else {
            toast.error(t('auth.resetPasswordError'), {
              description: error.message
            })
          }
        } else {
          toast.error(t('auth.resetPasswordError'))
        }
      }
    } catch (error) {
      toast.error(t('auth.resetPasswordError'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn(
      "min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-background-primary dark:bg-background-dark-primary",
      language === 'ar' ? 'font-arabic' : 'font-sans'
    )}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="p-3 bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl shadow-sm">
            <img 
              src={theme === 'dark' ? LogoDark : LogoLight}
              alt="Flows.Cash Logo"
              className="h-12 w-auto"
            />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-semibold bg-gradient-to-r from-text-primary to-text-secondary dark:from-text-dark-primary dark:to-text-dark-secondary bg-clip-text text-transparent">
          {t('auth.resetPassword')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary dark:text-text-dark-secondary">
          {t('auth.resetPasswordDescription')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 py-8 px-4 shadow-sm sm:rounded-xl sm:px-10">
          <form className="space-y-6" onSubmit={handleResetPassword}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary">
                {t('auth.email.label')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email.placeholder')}
                  className="w-full px-3 py-2 bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl h-11 text-text-primary dark:text-text-dark-primary focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark disabled:opacity-50"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl h-11 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-primary-dark dark:bg-primary-dark dark:hover:bg-primary-dark/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('auth.sendingResetLink')}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    {t('auth.sendResetLink')}
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/auth/login"
              className="text-sm text-secondary dark:text-primary-dark hover:text-secondary/80 dark:hover:text-primary-dark/80 hover:underline focus:outline-none transition-colors"
            >
              {t('auth.backToLogin')}
            </Link>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
              {t('auth.resetPasswordNote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
} 